import { Box, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { useLocale } from '@repo/i18n';
import { FacilitiesAttributes, Product, Translations } from '@repo/types';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { getImportantFacilities } from '@repo/widget-utils/accommodations/accommodationUtils';
import {
    SvgIconComponent,
    Accessible,
    Help,
    Kitchen,
    LocalDining,
    PersonOutlined,
    Pets,
    SmokingRooms,
    Wifi,
} from '@mui/icons-material';

import type { JSX } from 'react';

type AccommodationOverviewFacilityIconsProps = {
    accommodation: Product;
    numberOfIcons?: number;
};

export default function AccommodationOverviewFacilityIcons({
    accommodation,
    numberOfIcons,
}: AccommodationOverviewFacilityIconsProps) {
    const { t } = useLocale();

    const notPhoneScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const iconCount = notPhoneScreen ? 5 : 3;
    const icons = getIconSet(
        accommodation,
        numberOfIcons ? numberOfIcons : iconCount,
        t,
        (icon, label, i) => <AccommodationOverviewIcon key={i} Icon={icon} label={label} />,
    );

    return (
        <Grid
            container
            sx={{
                containerType: 'inline-size',
                width: '100%',
            }}
        >
            <Grid
                container
                justifyContent="center"
                sx={{
                    // MUI Grid v2 (v7) only forwards a fixed allowlist of
                    // props; bare `display="grid"` / `gridTemplateColumns="…"`
                    // attributes were silently dropped during the v5→v7
                    // migration, leaving this row falling back to the Grid's
                    // default flex layout (which made the icon-label pairs
                    // stack vertically). Setting them via `sx` puts them back
                    // on the element so CSS Grid takes over again.
                    //
                    // Also force `width: 100%` — v5 Grid containers stretched
                    // to fill their parent automatically; v2 lets them shrink
                    // to content width, which here means the auto-fit grid
                    // collapses to a single ~50px column and we get vertical
                    // stacking again. Stretching to the card's body width
                    // restores the multi-column layout.
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(50px, 1fr))',
                    width: '100%',
                }}
            >
                {icons}
            </Grid>
        </Grid>
    );
}

function AccommodationOverviewIcon(props: { Icon: SvgIconComponent; label: string }) {
    const { Icon, label } = props;
    const customizations = useCustomizations();

    return (
        <Grid
            container
            pb={2}
            sx={{
                width: 'auto',
                boxSizing: 'border-box',
                ['&:last-of-type']: {
                    paddingRight: 0,
                },
            }}
        >
            <Grid container direction="column" size="grow" alignItems="center">
                <Icon
                    sx={{
                        fontSize: '1.5em',
                        ['@container (width > 350px)']: {
                            fontSize: '2.5em',
                        },
                    }}
                />
                <Typography
                    variant="h5"
                    pt={1}
                    fontSize={customizations.fontSize * 0.85}
                    sx={{
                        // Reserve a 2-line height so single-line labels (e.g.
                        // "1 person") and 2-line labels (e.g.
                        // "Trådløst internett" wrapping to "Trådløst /
                        // internett" in narrow columns) occupy the same
                        // vertical space. Without this, short labels leave a
                        // big empty gap at the bottom of their cell while the
                        // wifi label fills it, making the row look ragged.
                        minHeight: '2.4em',
                        textAlign: 'center',
                        lineHeight: 1.2,
                        '& *': {
                            whiteSpace: 'break-spaces',
                        },
                    }}
                >
                    <Box>{label}</Box>
                </Typography>
            </Grid>
        </Grid>
    );
}

export function getIconSet(
    accommodation: Product,
    numberOfIcons: number,
    t: Translations,
    iconDecorator: (icon: SvgIconComponent, label: string, i: number) => JSX.Element,
) {
    const guestIcons = getGuestIcons(t, accommodation.capacity) ?? [];
    const importantFacilities = getImportantFacilities(
        accommodation.accommodationAttributes?.facilities,
    );
    const facilityIcons = importantFacilities.map((facility) => toIconData(facility, t));

    const icons = [...guestIcons, ...facilityIcons]
        .slice(0, numberOfIcons)
        .map(({ icon, label }, i) => iconDecorator(icon, label, i));

    return icons;
}

function toIconData(facility: keyof FacilitiesAttributes, t: Translations) {
    switch (facility) {
        case 'hasWifi':
            return { icon: Wifi, label: t.has_wifi };
        case 'isBreakfastIncluded':
            return { icon: LocalDining, label: t.breakfast };
        case 'isWheelchairAccessible':
            return { icon: Accessible, label: t.accessible };
        case 'isPetsAllowed':
            return { icon: Pets, label: t.pets };
        case 'hasKitchen':
            return { icon: Kitchen, label: t.kitchen };
        case 'isSmokingAllowed':
            return { icon: SmokingRooms, label: t.smoking };
        default:
            return { icon: Help, label: t.unknown };
    }
}

function getGuestIcons(t: Translations, count?: number) {
    if (!count) return [];
    return [
        {
            icon: PersonOutlined,
            label: `${count} ${t.person.num(count)}`,
        },
    ];
}
